/**
 * Builds the {@link TokenImageCtx} the board's token resolver (`lib/tokenArt.ts`'s `tokenImageFor`)
 * reads: the live roster, the compendium's NPC roles, and this viewer's disclosure.
 *
 * Masking goes through the real render-time projection (`projectForDisplay` +
 * `isMaskedCombatant`), the same call the inspector and the token chrome make, so the board can never
 * disagree with them about which hostiles are masked for this viewer.
 */
import { projectForDisplay, type ActorRole, type EncounterState, type RosterState } from "@lancer-vtt/domain";
import type { TokenImageCtx } from "../tokenArt";
import { isMaskedCombatant } from "./disclosure";

export type TokenImageCtxArgs = {
  roster: RosterState | null;
  /** Structurally a `Compendium`, narrowed to the one lookup the ring glyph needs. */
  compendium: { getById(type: "npc_class", id: string): { role?: string } | undefined } | null;
  state: EncounterState;
  viewer: { role: ActorRole; viewerSlot: string | null };
};

export function buildTokenImageCtx(args: TokenImageCtxArgs): TokenImageCtx {
  const { roster, compendium, state, viewer } = args;
  return {
    pilots: roster?.pilots,
    isMasked: (id) => {
      const combatant = state.combatants[id];
      if (combatant === undefined) return false;
      return isMaskedCombatant(projectForDisplay({ combatant, viewerSlot: viewer.viewerSlot, role: viewer.role, state }));
    },
    npcRole: compendium ? (classId) => compendium.getById("npc_class", classId)?.role : undefined,
  };
}
